import { SmartButton } from '@tunasong/icons'
import { shortUuid } from '@tunasong/models'
import { MelodyLib } from '@tunasong/music-lib'
import type { Melody, MelodyChordLane, Scale } from '@tunasong/schemas'
import { Button, Drawer, Menu, MenuContent, MenuItem, MenuTrigger, useTunaMediaQuery } from '@tunasong/ui-lib'
import { useEffect, useEffectEvent, useId, useReducer, useRef, useState } from 'react'
import type { CSSProperties, FC, KeyboardEvent, UIEvent } from 'react'
import { Keyboard } from '../keyboard/keyboard.js'
import type { KeyboardNoteInfo } from '../keyboard/keyboard.js'
import { MelodyCapture } from '../melody-capture/melody-capture.js'
import { MelodyPracticeMenu } from '../pitch-trainer-phrases/melody-practice-menu.js'
import { PhrasePracticeDialog } from '../pitch-trainer-phrases/phrase-practice-dialog.js'
import { spokenText } from '../theory-copy/spoken-text.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryLevel } from '../theory-copy/use-theory-copy.js'
import { ChordLane, ChordLaneGuess } from './chord-lane.js'
import { GhostBar } from './ghost-bar.js'
import { ghostReducer, idleGhost, plainGhostFailureLine, type GhostResult } from './ghost-options.js'
import { midiInsert } from './melody-midi-insert.js'
import styles from './melody-editor.module.css'
import { MelodyRoll } from './melody-roll.js'
import { rollBeats, stepToBeat } from './melody-roll-geometry.js'
import { MelodyShortcutsHelp } from './melody-shortcuts-help.js'
import { MelodyTransport } from './melody-transport.js'
import { TakesList } from './takes-list.js'
import { usePrefersReducedMotion } from './use-prefers-reduced-motion.js'
import { MelodyRollHeader } from './melody-roll-header.js'
import { selectionActions, type SelectionAction } from './selection-actions.js'
import { SelectionBar } from './selection-bar.js'
import { canLengthen, canShorten, lengthenSelection, selectionLoop, shortenSelection } from './selection-edits.js'
import { useMelodyMidiInsert } from './use-melody-midi-insert.js'
import { useMelodyRollPlayback } from './use-melody-roll-playback.js'
import { VelocityLane } from './velocity-lane.js'

/**
 * The full melody editor: header, roll, velocity lane, on-screen keyboard, MIDI insert, transport.
 *
 * It owns exactly three pieces of state, none of which belong to the melody: the insert cursor,
 * the selected note, and the "quantized" A/B toggle. Everything else is a pure `MelodyLib` op
 * against the melody the caller passed in, returned through `onChange` - so the entity is the one
 * copy of the truth and there is no local draft to reconcile.
 *
 * The invariant readout is deliberately visible rather than silently repaired: a melody arriving
 * off-grid means something upstream (a converter, or one day the §5 quantizer) is wrong, and
 * quietly snapping it would hide that.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4
 */

export interface MelodyEditorProps {
  melody: Melody
  onChange: (melody: Melody) => void
  /**
   * Where a capture's recording is filed - the melody's own entity, or the document an embedded
   * melody sits in. Absent means the take's audio is saved without a parent.
   */
  captureParentId?: string
  /** What the Practice menu's phrases are named after; defaults to the melody's name */
  practiceName?: string
  /**
   * The section's chart chords, which the chord lane follows; null outside a section, and absent where
   * there is no document at all. Either way the lane is then the melody's own (melody toolkit plan M1).
   */
  chart?: MelodyLib.ChartChord[] | null
  /** The song's or the section's key: the drone's, when the melody has none of its own (spec §7, §8) */
  songKey?: Scale
  /**
   * Where ghost options come from (melody toolkit M4; spec §6): Suggest a melody, in a document. Absent where there is
   * no AI, and then the editor has no ghost options.
   */
  suggest?: MelodySuggestSource
  /**
   * Keep going (melody toolkit M7.2; spec §6): the AI continues the selected phrase, in the selection bar's slot.
   * Absent where there is no AI.
   */
  keepGoing?: MelodyKeepGoingSource
  /**
   * The singer's range (melody toolkit M8a; spec §5.7): the calibrated range, else the voice type they chose. Absent where
   * there is no singer to ask, and then nothing is said about range.
   */
  range?: MelodyRangeSource
  /** The standalone sketch opens with Sing an idea focused (melody toolkit M9, spec §7) */
  autoFocusSing?: boolean
  /** The standalone sketch: its empty chord lane offers `Add chords` (melody toolkit M9.2, spec §7) */
  canAddChords?: boolean
  className?: string
}

export interface MelodyKeepGoingSource {
  /** Ask for continuations of the selected notes, by index. A failure resolves with its line. */
  request: (selection: number[]) => Promise<GhostResult>
  /** Why it can't run for these notes, e.g. "No room after this phrase to keep going."; null when it can */
  disabledReasonFor: (selection: number[]) => string | null
}

export interface MelodyRangeSource {
  /** The calibrated range from Pitch Trainer, in MIDI; it wins over a voice type */
  calibrated: { low: number; high: number } | null
  voiceType: MelodyLib.VoiceType | null
  /** Saves the voice type the singer picks in Show my range */
  onVoiceTypeChange: (voiceType: MelodyLib.VoiceType) => void
}

/** The voice types as Show my range lists them (spec §11) */
const voiceTypeNames: Record<MelodyLib.VoiceType, string> = {
  soprano: 'Soprano',
  alto: 'Alto',
  tenor: 'Tenor',
  baritone: 'Baritone',
  bass: 'Bass',
}

/** A range edge in words: the key's default spelling, accidentals spoken ("G flat 3"); a measured edge rounds */
const edgeNames = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B']
const edgeWords = (midi: number) => {
  const rounded = Math.round(midi)
  const name = edgeNames[((rounded % 12) + 12) % 12] ?? 'C'
  const octave = Math.floor(rounded / 12) - 1
  return name.length > 1 ? `${spokenText(name)} ${octave}` : `${name}${octave}`
}

/** Spec §11's offer, and Brian's two-octave wording (2026-10-31) */
const offerText = (octaves: number) => {
  const size = Math.abs(octaves) === 1 ? 'an octave' : 'two octaves'
  return `Try it ${size} ${octaves < 0 ? 'lower' : 'higher'}`
}

export interface MelodySuggestSource {
  /** Ask for options. A failure resolves with its line; a request that throws shows the plain failure line. */
  request: () => Promise<GhostResult>
  /** True when a request was asked for from elsewhere (Assist › Suggest a melody): it starts once the editor shows */
  isRequested?: boolean
  /** Called when the editor takes that request, so it isn't started again */
  onRequestTaken?: () => void
  /** Why a request can't run now, e.g. "Needs a connection."; null or absent when it can */
  disabledReason?: string | null
}

/** Typing in these is typing, never a shortcut */
const isTextEntry = (target: EventTarget) =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement

/** Brian's Q2: Sequence and Mirror need a key; the reused M3 line says why */
const noKeyReason = 'Set a key to see how notes relate.'

/** The phone breakpoint, as the spec's §9 and the CSS have it */
const phoneQuery = '(max-width: 599.95px)'

/** A written note the option leaves as it was: drawn solid, not dimmed */
const isSameNote = (a: Melody['notes'][number], b: Melody['notes'][number]) =>
  a.midi === b.midi &&
  Math.abs(a.onset - b.onset) < MelodyLib.GRID_TOLERANCE &&
  Math.abs(a.duration - b.duration) < MelodyLib.GRID_TOLERANCE

/** The keys `handlePlotKeyDown` answers to, as `aria-keyshortcuts` names them */
const plotKeyShortcuts = 'Space L C M R [ ] Enter Escape'

export const MelodyEditor: FC<MelodyEditorProps> = props => {
  const {
    melody,
    onChange,
    captureParentId,
    practiceName,
    chart,
    songKey,
    suggest,
    keepGoing,
    range,
    autoFocusSing = false,
    canAddChords = false,
    className,
  } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const [cursorBeats, setCursorBeats] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  /** The selected notes (melody toolkit M6), by index; indexes the melody no longer has are dropped as it is drawn */
  const [selectionState, setSelection] = useState<number[]>([])
  const [quantized, setQuantized] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const laneTrackRef = useRef<HTMLDivElement>(null)
  const rollScrollerRef = useRef<HTMLDivElement>(null)
  const velocityScrollerRef = useRef<HTMLDivElement>(null)
  /** Spec §5.1: Chords and Melody both on to start with; Loop and the drone off */
  const [isChordsOn, setChordsOn] = useState(true)
  const [isMelodyOn, setMelodyOn] = useState(true)
  const [isLooping, setLooping] = useState(false)
  const [isDroneOn, setDroneOn] = useState(false)
  /** Ghost options (spec §6): local to this editor, never written to the melody until Keep */
  const [ghost, dispatchGhost] = useReducer(ghostReducer, idleGhost)
  /** The ghost notes Play this option last started; anything else started since clears it (GH #2978) */
  const [auditionedNotes, setAuditionedNotes] = useState<Melody['notes'] | null>(null)
  /**
   * Which request the editor is waiting for. Discard while writing leaves the request running, so its answer can
   * land after a later request started, and must not fill it.
   */
  const requestCount = useRef(0)
  /** Exact intervals (M6): on until the selection clears (Joni's layout) */
  const [isExact, setExact] = useState(false)
  /** On a phone the selection actions open in a bottom sheet (spec §9) */
  const isPhone = useTunaMediaQuery(phoneQuery)
  const [isSheetOpen, setSheetOpen] = useState(false)
  const liveRegionId = useId()
  const keepGoingReasonId = useId()
  /** What Try again asks again: Suggest, or Keep going for the notes it was asked for */
  const [lastAsked, setLastAsked] = useState<{ kind: 'suggest' } | { kind: 'keep-going'; phrase: number[] }>({
    kind: 'suggest',
  })

  /** The lane is resolved on every render, so a chord typed into the chart shows up at once (§8) */
  const lane = MelodyLib.resolveChordLane(chart ?? null, melody.chordLane, melody.tempo.meter)
  const hasChords = lane.chords.length > 0
  /** The key the drone plays in and the cues are read in: the melody's own, else the section's or song's */
  const droneKey = melody.key ?? songKey

  /**
   * Theory cues (melody toolkit M3; spec §5.3), at the user's Theory cues level: off hides them, and
   * Advanced adds each note's degree number. Deterministic and offline.
   */
  const theoryLevel = useTheoryLevel()
  const isCueing = theoryLevel !== 'off'
  const roles = isCueing ? MelodyLib.noteRoles(melody, lane, droneKey) : []
  const whyLines =
    selectedIndex === null || theoryLevel === 'off'
      ? []
      : roles
          .filter(role => role.noteIndex === selectedIndex)
          .map(role => MelodyLib.whyLine(role, { level: theoryLevel, ...(droneKey ? { key: droneKey } : {}) }))
  /** Spec §8: with no chords and no key there is nothing to read the notes against */
  const isMissingKey = isCueing && !hasChords && !droneKey
  const totalBeats = rollBeats(melody, MelodyLib.laneEnd(lane))
  const handleLaneChange = (chordLane: MelodyChordLane) => onChange({ ...melody, chordLane })
  const selection = selectionState.filter(index => index < melody.notes.length)
  const isSelecting = selection.length >= 2

  /** Fewer than two notes selected: no actions, so Exact intervals and the sheet go */
  const changeSelection = (indices: number[]) => {
    setSelection(indices)
    if (indices.length < 2) {
      setExact(false)
      setSheetOpen(false)
    }
  }

  /** D7: a lane chord selects the notes whose onsets fall in it */
  const handleChordSelect = (index: number) => {
    const chord = lane.chords[index]
    if (!chord) {
      return
    }
    const end = chord.onset + chord.duration
    changeSelection(
      melody.notes.flatMap((note, noteIndex) =>
        note.onset > chord.onset - MelodyLib.GRID_TOLERANCE && note.onset < end - MelodyLib.GRID_TOLERANCE
          ? [noteIndex]
          : []
      )
    )
  }

  /** D6: with notes selected, Loop loops their bars; an option being auditioned plays over the whole lane */
  const loop = isLooping && isSelecting && ghost.status === 'idle' ? selectionLoop(melody, lane, selection) : null
  const playback = useMelodyRollPlayback({
    melody: loop?.melody ?? melody,
    quantized,
    lane: loop?.lane ?? lane,
    /** With no chords, the Chords toggle has nothing to play: the drone, when it is on, stands in */
    isChordsOn: hasChords ? isChordsOn : isDroneOn,
    isMelodyOn,
    isLooping,
    ...(droneKey ? { drone: droneKey } : {}),
  })
  const isReducedMotion = usePrefersReducedMotion()
  /** A looped selection plays from 0: its play head is drawn where its bars are */
  const loopOffsetMs = loop ? loop.startBeats * MelodyLib.beatMs(melody.tempo.bpm) : 0
  const playedMs = playback.playheadMs === null ? null : playback.playheadMs + loopOffsetMs
  const playheadMs = isReducedMotion ? stepToBeat(playedMs, melody.tempo.bpm) : playedMs

  /** The option being shown, its notes as a melody, and its cues: the cues apply to ghost notes too (spec §6) */
  const shownOption = ghost.status === 'showing' ? ghost.options[ghost.index] : undefined
  const ghostMelody = shownOption ? { ...melody, notes: shownOption.notes } : null
  const allGhostRoles = ghostMelody && isCueing ? MelodyLib.noteRoles(ghostMelody, lane, droneKey) : []
  /** A transform's option draws only the notes it writes, and dims only the written notes it changes (M6) */
  const written = shownOption?.selection
  const ghostNotes = ghostMelody
    ? written
      ? written.flatMap(index => ghostMelody.notes[index] ?? [])
      : ghostMelody.notes
    : []
  const ghostRoles = written
    ? allGhostRoles.flatMap(role => {
        const position = written.indexOf(role.noteIndex)
        return position < 0 ? [] : [{ ...role, noteIndex: position }]
      })
    : allGhostRoles
  const dimmed =
    written && ghostMelody
      ? melody.notes.flatMap((note, index) => (ghostMelody.notes.some(other => isSameNote(other, note)) ? [] : [index]))
      : undefined
  /** Keep replaces something: any written notes under a whole-line option, the changed ones under a transform */
  const isReplacing = dimmed ? dimmed.length > 0 : melody.notes.length > 0
  /** The bar says Stop only while the option it shows is what is playing */
  const isOptionPlaying = playback.isPlaying && shownOption !== undefined && auditionedNotes === shownOption.notes

  const playOption = () => {
    if (!ghostMelody) {
      return
    }
    setAuditionedNotes(ghostMelody.notes)
    playback.playMelody(ghostMelody)
  }

  /** The melody itself, or a take: no longer the option playing */
  const playOwn = (start: () => void) => {
    setAuditionedNotes(null)
    start()
  }

  /** Ask for options; only the latest request's answer is shown (GH #2978) */
  const runRequest = (ask: () => Promise<GhostResult>) => {
    requestCount.current += 1
    const current = requestCount.current
    const resolve = (result: GhostResult) => {
      if (current === requestCount.current) {
        dispatchGhost({ type: 'resolve', result })
      }
    }
    dispatchGhost({ type: 'start' })
    ask()
      .then(resolve)
      .catch(() => resolve({ kind: 'failed', line: plainGhostFailureLine, isNotCounted: false }))
  }

  const requestOptions = () => {
    if (!suggest) {
      return
    }
    setLastAsked({ kind: 'suggest' })
    runRequest(() => suggest.request())
  }

  /** M7.2: continue the selected notes; nothing is asked while the source says why it can't */
  const keepGoingReason = keepGoing ? keepGoing.disabledReasonFor(selection) : null
  const requestKeepGoing = (phrase: number[]) => {
    if (!keepGoing || keepGoing.disabledReasonFor(phrase)) {
      return
    }
    setLastAsked({ kind: 'keep-going', phrase })
    runRequest(() => keepGoing.request(phrase))
  }

  /** Try again asks what failed again: a failed Keep going is retried as Keep going, for the same notes */
  const retry = () => (lastAsked.kind === 'keep-going' ? requestKeepGoing(lastAsked.phrase) : requestOptions())
  const retryDisabledReason =
    lastAsked.kind === 'keep-going'
      ? (keepGoing?.disabledReasonFor(lastAsked.phrase) ?? null)
      : (suggest?.disabledReason ?? null)

  /** A selection action on the selected notes, by scale step or exact intervals; null when it is unavailable */
  const transform = (action: SelectionAction, exact: boolean) => {
    const options = { exact }
    switch (action) {
      case 'repeat':
        return MelodyLib.repeatSelection(melody, selection)
      case 'repeat-fit':
        return MelodyLib.repeatFitToChords(melody, selection, lane, droneKey)
      case 'sequence-up':
        return MelodyLib.sequenceSelection(melody, selection, droneKey, 'up', options)
      case 'sequence-down':
        return MelodyLib.sequenceSelection(melody, selection, droneKey, 'down', options)
      case 'mirror':
        return MelodyLib.mirrorSelection(melody, selection, droneKey, options)
      case 'shift-earlier':
        return MelodyLib.shiftSelection(melody, selection, 'earlier', lane)
      case 'shift-later':
        return MelodyLib.shiftSelection(melody, selection, 'later', lane)
    }
  }

  const needsKey = (action: SelectionAction) =>
    action === 'sequence-up' || action === 'sequence-down' || action === 'mirror'
  const availability = Object.fromEntries(
    selectionActions.map(({ action }) => [
      action,
      isSelecting && !droneKey && needsKey(action)
        ? { isAvailable: false, reason: noKeyReason }
        : { isAvailable: isSelecting && transform(action, isExact) !== null },
    ])
  ) as Record<SelectionAction, { isAvailable: boolean; reason?: string }>

  /** The result shows as a ghost option, named after its action, through the same path as AI options (spec §6) */
  const handleAction = (action: SelectionAction, { isExact: exact }: { isExact: boolean }) => {
    const result = transform(action, exact)
    if (!result) {
      return
    }
    /** A Suggest request still in flight must not replace it */
    requestCount.current += 1
    const label = selectionActions.find(entry => entry.action === action)?.label ?? ''
    dispatchGhost({ type: 'start' })
    dispatchGhost({
      type: 'resolve',
      result: { kind: 'options', options: [{ notes: result.notes, label, selection: result.selection }] },
    })
  }

  /** Assist › Suggest a melody asked for options before the editor showed: start once, and say it was taken */
  const isRequested = suggest?.isRequested ?? false
  const takeRequest = useEffectEvent(() => {
    suggest?.onRequestTaken?.()
    requestOptions()
  })
  useEffect(() => {
    if (isRequested) {
      takeRequest()
    }
  }, [isRequested])

  /** After Keep or Discard, focus goes back to the roll, where the options were (spec §10) */
  const focusRoll = () => rollScrollerRef.current?.querySelector<SVGSVGElement>('svg[role="group"]')?.focus()

  /** Keep writes the option as one edit: one `onChange`, so one undo step */
  const keepOption = () => {
    if (!ghostMelody) {
      return
    }
    onChange(ghostMelody)
    /** Spec §6, 4a: the notes a transform wrote are the selection, so the next action applies to them */
    if (shownOption?.selection) {
      changeSelection(shownOption.selection)
    }
    dispatchGhost({ type: 'close' })
    /** Joni's #3013 review, P2 4: the sheet closes so the result is seen; the selection stays, to reopen it */
    setSheetOpen(false)
    focusRoll()
  }

  const selectionBar = (
    <SelectionBar
      count={selection.length}
      availability={availability}
      isExact={isExact}
      onExactChange={setExact}
      onAction={handleAction}
      canLengthen={canLengthen(melody, selection)}
      canShorten={canShorten(melody, selection)}
      onLonger={() => onChange(lengthenSelection(melody, selection))}
      onShorter={() => onChange(shortenSelection(melody, selection))}
      {...(keepGoing
        ? {
            keepGoing: (
              <>
                {/* Joni's layout: the AI source, led by the sparkles glyph, set off from the deterministic actions */}
                <Button
                  size="small"
                  startIcon={<SmartButton fontSize="small" />}
                  /** Named by its words alone: the glyph is decoration */
                  aria-label="Keep going"
                  onClick={() => requestKeepGoing([...selection])}
                  aria-disabled={keepGoingReason ? 'true' : undefined}
                  aria-describedby={keepGoingReason ? keepGoingReasonId : undefined}
                >
                  Keep going
                </Button>
                {keepGoingReason ? (
                  <span id={keepGoingReasonId} className={styles.keepGoingReason}>
                    {keepGoingReason}
                  </span>
                ) : null}
              </>
            ),
          }
        : {})}
    />
  )

  const discardOptions = () => {
    dispatchGhost({ type: 'close' })
    setSheetOpen(false)
    focusRoll()
  }

  /** M8a: the band is the calibrated range, else the voice type's, approximate; none without either */
  const [isMeasuring, setMeasuring] = useState(false)
  const rangeRowRef = useRef<HTMLDivElement>(null)
  const rangeDescriptionId = useId()
  /** Joni's #3026 review: after measuring, focus goes back to what opened it */
  const closeMeasuring = () => {
    setMeasuring(false)
    rangeRowRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }
  const band = range?.calibrated
    ? { ...range.calibrated, label: 'Your range' }
    : range?.voiceType
      ? {
          ...MelodyLib.voiceTypeBands[range.voiceType],
          label: `Approximate range for ${voiceTypeNames[range.voiceType]}`,
        }
      : null
  /** Range never refuses a note: it only offers an octave, through the ghost layer, never while an option shows */
  const fit = band && ghost.status === 'idle' ? MelodyLib.rangeFit(melody.notes, band) : null
  const offer = fit?.offer ?? null
  const showOffer = () => {
    if (!offer) {
      return
    }
    requestCount.current += 1
    dispatchGhost({ type: 'start' })
    dispatchGhost({
      type: 'resolve',
      result: { kind: 'options', options: [{ notes: offer.notes, label: offerText(offer.octaves) }] },
    })
  }

  const ghostBar = (
    <GhostBar
      state={ghost}
      liveRegionId={liveRegionId}
      onPrevious={() => dispatchGhost({ type: 'previous' })}
      onNext={() => dispatchGhost({ type: 'next' })}
      onPlay={playOption}
      onStop={playback.stop}
      isOptionPlaying={isOptionPlaying}
      onKeep={keepOption}
      onDiscard={discardOptions}
      onRetry={retry}
      retryDisabledReason={retryDisabledReason}
      /** Keep replaces notes: say so when there are notes it would replace (Joni's review) */
      isReplacing={isReplacing}
      {...(dimmed ? { replacedCount: dimmed.length } : {})}
    />
  )

  useMelodyMidiInsert({ melody, cursorBeats, onChange, onCursorChange: setCursorBeats })

  const handleKeyboardNote = ({ midiNumber }: KeyboardNoteInfo) => {
    /* An on-screen key has no velocity, so it takes the same default a converted suggestion does. */
    const result = midiInsert(melody, cursorBeats, midiNumber, MelodyLib.SUGGESTION_VELOCITY)
    onChange(result.melody)
    setCursorBeats(result.cursorBeats)
  }

  const handleChordsChange = (isOn: boolean) => (hasChords ? setChordsOn(isOn) : setDroneOn(isOn))

  /**
   * Spec §10: the roll's shortcuts, active only while focus is in the roll and its lanes. A modifier held
   * means the browser's shortcut, and a text field is typing.
   */
  const handlePlotKeyDown = (event: KeyboardEvent) => {
    /** A button keeps its own keys: Space presses it (Joni, M1–M5 review) */
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      isTextEntry(event.target) ||
      event.target instanceof HTMLButtonElement
    ) {
      return
    }
    /** With ghost options up, [ and ] cycle them, Enter keeps one and Esc discards them (spec §10) */
    const ghostActions: Record<string, () => void> =
      ghost.status === 'idle'
        ? {}
        : {
            Escape: discardOptions,
            ...(ghost.status === 'showing'
              ? {
                  '[': () => dispatchGhost({ type: 'previous' }),
                  ']': () => dispatchGhost({ type: 'next' }),
                  Enter: keepOption,
                }
              : {}),
          }
    const actions: Record<string, () => void> = {
      ' ': () => (playback.isPlaying ? playback.stop() : playOwn(playback.play)),
      l: () => setLooping(!isLooping),
      c: () => handleChordsChange(!(hasChords ? isChordsOn : isDroneOn)),
      m: () => setMelodyOn(!isMelodyOn),
      r: () => setCapturing(true),
      /** Joni's #3013 review, P2 3: Esc clears a selection before it leaves the roll; the roll cancels a box first */
      Escape: () => (selection.length > 0 ? changeSelection([]) : rootRef.current?.focus()),
      ...ghostActions,
    }
    const action = actions[event.key.length === 1 ? event.key.toLowerCase() : event.key]
    if (!action) {
      return
    }
    event.preventDefault()
    action()
  }

  const issues = MelodyLib.validateMelody(melody)

  const rollBars = totalBeats / MelodyLib.barBeats(melody.tempo.meter)
  /** The sticky lane has no scrollbar of its own: it shows the beats the roll scrolled to */
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget
    if (laneTrackRef.current) {
      laneTrackRef.current.scrollLeft = scrollLeft
    }
    if (velocityScrollerRef.current && velocityScrollerRef.current.scrollLeft !== scrollLeft) {
      velocityScrollerRef.current.scrollLeft = scrollLeft
    }
  }

  /** The velocity lane scrolls on its own too (a finger pans it): the roll, and so the lane, follow */
  const handleVelocityScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget
    if (rollScrollerRef.current && rollScrollerRef.current.scrollLeft !== scrollLeft) {
      rollScrollerRef.current.scrollLeft = scrollLeft
    }
  }

  return (
    <div ref={rootRef} tabIndex={-1} className={className ? `${styles.root} ${className}` : styles.root}>
      <MelodyRollHeader melody={melody} onChange={onChange} quantized={quantized} onQuantizedChange={setQuantized} />

      {/* Spec §5.5: the takes, under the header. Each change is one whole melody, so one undo step */}
      <TakesList
        takes={melody.takes ?? []}
        {...(melody.currentTakeId ? { currentTakeId: melody.currentTakeId } : {})}
        onAudition={id => {
          const take = melody.takes?.find(entry => entry.id === id)
          if (take) {
            playOwn(() => playback.playMelody({ ...melody, notes: take.notes }))
          }
        }}
        onUse={id => onChange(MelodyLib.switchToTake(melody, id, shortUuid()))}
        onRename={(id, name) => onChange(MelodyLib.renameTake(melody, id, name))}
        onSaveAsTake={() => onChange(MelodyLib.saveAsTake(melody, shortUuid()))}
      />

      <div className={styles.bar}>
        <MelodyTransport
          className={styles.transportBar}
          isPlaying={playback.isPlaying}
          onPlay={() => playOwn(playback.play)}
          onStop={playback.stop}
          isLooping={isLooping}
          onLoopChange={setLooping}
          bpm={melody.tempo.bpm}
          hasNotes={melody.notes.length > 0}
          hasChords={hasChords}
          isChordsOn={isChordsOn}
          onChordsChange={setChordsOn}
          isMelodyOn={isMelodyOn}
          onMelodyChange={setMelodyOn}
          onSingIdea={() => setCapturing(true)}
          autoFocusSing={autoFocusSing}
        />
        {/* Spec §9; Joni's layout: on a phone the count keeps the selection in view and opens its actions */}
        {isPhone && isSelecting ? (
          <Button aria-label={`Actions for ${selection.length} selected notes`} onClick={() => setSheetOpen(true)}>
            {`${selection.length} notes`}
          </Button>
        ) : null}
        <MelodyShortcutsHelp />
      </div>

      {/* Spec §5.7: the range, said in words, and the octave offer; the band on the roll is the picture */}
      {range ? (
        <div ref={rangeRowRef} className={styles.range}>
          {/* Joni's #3026 review: the band is a picture, so the range is said in words too */}
          {band ? (
            <span
              className={styles.rangeWords}
            >{`${band.label}: ${edgeWords(band.low)} to ${edgeWords(band.high)}`}</span>
          ) : null}
          {range.calibrated ? (
            <span id={rangeDescriptionId} className={styles.rangeWords}>
              Your measured range is used while you have one.
            </span>
          ) : null}
          {/* Joni's #3026 review: once a band shows, the same choices stay behind a quiet Change range */}
          <Menu>
            <MenuTrigger
              render={
                <button
                  type="button"
                  className={styles.rangeLink}
                  aria-describedby={range.calibrated ? rangeDescriptionId : undefined}
                >
                  {band ? 'Change range' : 'Show my range'}
                </button>
              }
            />
            <MenuContent>
              <MenuItem onClick={() => setMeasuring(true)}>Measure your range</MenuItem>
              {MelodyLib.voiceTypes.map(voiceType => (
                <MenuItem key={voiceType} onClick={() => range.onVoiceTypeChange(voiceType)}>
                  {voiceTypeNames[voiceType]}
                </MenuItem>
              ))}
            </MenuContent>
          </Menu>
          {offer ? (
            <>
              <span className={styles.rangeNote}>
                {offer.octaves < 0 ? 'Most of this sits above your range.' : 'Most of this sits below your range.'}
              </span>
              <Button size="small" onClick={showOffer}>
                {offerText(offer.octaves)}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      <PhrasePracticeDialog
        open={isMeasuring}
        onClose={closeMeasuring}
        sources={[]}
        onEdit={closeMeasuring}
        start="calibrate"
      />

      {/*
       * Spec §6; Joni's layout (2026-10-31): one row, the selection bar while two or more notes are selected and no
       * option shows, else the ghost bar, which is always there for its live region. On a phone both are in the sheet.
       */}
      {!isPhone && isSelecting && ghost.status === 'idle' ? selectionBar : null}
      {isPhone && isSheetOpen ? null : ghostBar}

      <Drawer open={isPhone && isSheetOpen} onClose={() => setSheetOpen(false)} anchor="bottom" width="auto">
        <div className={styles.sheet}>
          {ghost.status === 'idle' ? selectionBar : null}
          <div className={styles.sheetFooter}>{ghostBar}</div>
        </div>
      </Drawer>

      <div className={styles.transport}>
        <span className={styles.cursor}>{`Insert at beat ${cursorBeats + 1}`}</span>
        <Button onClick={() => setCursorBeats(0)}>Cursor to start</Button>
        {/*
         * Practice sits here rather than in `MelodyRollHeader`: that header holds the melody's FIELDS, and
         * a control that neither reads nor writes one does not belong there.
         */}
        {melody.notes.length > 0 ? (
          <MelodyPracticeMenu melody={melody} sectionName={practiceName ?? melody.name ?? 'Melody'} />
        ) : null}
      </div>

      {/* Spec §5.2: Sing an idea is the capture, sung over the lane's chords (or the drone) */}
      <MelodyCapture
        melody={melody}
        open={capturing}
        parentId={captureParentId}
        /* Plan D2: a capture lands as notes and as a new take, and the notes there were are kept */
        onCapture={captured =>
          onChange(MelodyLib.addCaptureTake(melody, captured, { kept: shortUuid(), capture: shortUuid() }))
        }
        onClose={() => setCapturing(false)}
        {...(hasChords ? { backing: { lane } } : isDroneOn && droneKey ? { backing: { lane, drone: droneKey } } : {})}
      />

      {issues.length > 0 ? (
        <ul className={styles.issues} role="status">
          {issues.map(issue => (
            <li key={`${issue.kind}-${issue.noteIndex}`}>{issue.message}</li>
          ))}
        </ul>
      ) : null}

      {/*
       * Spec §9: below 600px the roll scrolls sideways, each bar wide enough for a 44px chord button
       * beside its edge handle, and the chord lane, roll and velocity lane scroll together. The lane sits
       * outside the scroller, whose overflow would hold it, so it sticks to the page while the roll scrolls
       * under it; it follows the scroller sideways (Joni, M1–M5 review).
       */}
      {/* Outside the lane's sideways scroll, so it never scrolls out of view on a phone (Joni) */}
      <ChordLaneGuess lane={lane} onChange={handleLaneChange} />

      {/* The roll's shortcuts work with focus anywhere in the lane, the roll or the velocity lane (§10) */}
      <div className={styles.plotArea} onKeyDown={handlePlotKeyDown}>
        {/* What the lane sticks within: the lane and the roll only, so it leaves with the roll (Joni) */}
        <div className={styles.stickArea}>
          <div ref={laneTrackRef} className={styles.laneTrack} style={{ '--roll-bars': rollBars } as CSSProperties}>
            <ChordLane
              className={styles.plotWidth}
              lane={lane}
              meter={melody.tempo.meter}
              totalBeats={totalBeats}
              isInSection={Array.isArray(chart)}
              onChange={handleLaneChange}
              canAddChords={canAddChords}
              onChordSelect={handleChordSelect}
              {...(droneKey ? { drone: { isOn: isDroneOn, onChange: setDroneOn } } : {})}
            />
          </div>

          <div ref={rollScrollerRef} className={styles.scroller} onScroll={handleScroll}>
            <div className={`${styles.plot} ${styles.plotWidth}`} style={{ '--roll-bars': rollBars } as CSSProperties}>
              <MelodyRoll
                melody={melody}
                onChange={onChange}
                quantized={quantized}
                playheadMs={playheadMs}
                cursorBeats={cursorBeats}
                onCursorChange={setCursorBeats}
                selectedIndex={selectedIndex}
                onSelect={setSelectedIndex}
                selection={selection}
                onSelectionChange={changeSelection}
                isLocked={ghost.status === 'showing'}
                describedBy={liveRegionId}
                minBeats={totalBeats}
                keyShortcuts={plotKeyShortcuts}
                roles={roles}
                {...(droneKey ? { cueKey: droneKey } : {})}
                isShadingStrongBeats={isCueing}
                isShowingDegreeNumbers={theoryLevel === 'advanced'}
                {...(band ? { rangeBand: band } : {})}
                {...(ghostMelody
                  ? {
                      ghost: {
                        notes: ghostNotes,
                        roles: ghostRoles,
                        isFading: !isReducedMotion,
                        ...(dimmed ? { dimmed } : {}),
                      },
                    }
                  : {})}
                {...(isCueing && hasChords
                  ? { rowFits: (beat: number) => MelodyLib.fitsAt(lane, droneKey, beat) }
                  : {})}
              />
            </div>
          </div>
        </div>

        <div ref={velocityScrollerRef} className={styles.scroller} onScroll={handleVelocityScroll}>
          <div className={`${styles.plot} ${styles.plotWidth}`} style={{ '--roll-bars': rollBars } as CSSProperties}>
            <VelocityLane
              melody={melody}
              onChange={onChange}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
              minBeats={totalBeats}
            />
          </div>
        </div>
      </div>

      {/* Spec §5.3: the why line, for the selected or focused note; one sentence per chord it is judged against */}
      {isMissingKey ? (
        <p className={styles.whyLine}>Set a key to see how notes relate.</p>
      ) : whyLines.length > 0 ? (
        <p className={styles.whyLine} data-testid="melody-why-line">
          {/* Each ♭ and ♯ announced as a word (Brian, 2026-10-23) */}
          <TheoryGlyphs>{whyLines.join(' ')}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.keyboard} data-testid="melody-keyboard">
        <Keyboard scale={melody.key ?? null} onPlayNote={handleKeyboardNote} />
      </div>
    </div>
  )
}
